import { AlertTriangle, CircleCheck, ShieldAlert, X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type AppAlertTone = "error" | "warning" | "success";

interface AlertEntry {
  id: string;
  message: string;
  title: string;
  tone: AppAlertTone;
  onDismiss?: () => void;
}

interface AlertContextValue {
  register: (entry: AlertEntry) => void;
  remove: (id: string) => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

export function upsertAlert(alerts: AlertEntry[], entry: AlertEntry) {
  const existing = alerts.findIndex((alert) => alert.id === entry.id);
  if (existing === -1) return [...alerts, entry];
  const next = [...alerts];
  next[existing] = entry;
  return next;
}

export function AppAlertProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<AlertEntry[]>([]);
  const dialog = useRef<HTMLElement>(null);
  const active = alerts[0];
  const register = useCallback((entry: AlertEntry) => {
    setAlerts((current) => upsertAlert(current, entry));
  }, []);
  const remove = useCallback((id: string) => {
    setAlerts((current) => current.filter((alert) => alert.id !== id));
  }, []);
  const context = useMemo(() => ({ register, remove }), [register, remove]);

  const dismiss = useCallback(() => {
    if (!active) return;
    active.onDismiss?.();
    remove(active.id);
  }, [active, remove]);

  useEffect(() => {
    if (!active) return undefined;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => { dialog.current?.focus(); });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismiss();
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const controls = Array.from(dialog.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialog.current.focus();
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previousFocus?.focus();
    };
  }, [active, dismiss]);

  return <AlertContext.Provider value={context}>
    {children}
    {active && createPortal(
      <div className="app-alert-layer">
        <button className="app-alert-layer__scrim" type="button" tabIndex={-1} aria-label="Dismiss message" onClick={dismiss} />
        <section
          className={`app-alert-dialog app-alert-dialog--${active.tone}`}
          ref={dialog}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="app-alert-title"
          aria-describedby="app-alert-message"
          tabIndex={-1}
        >
          <button className="app-alert-dialog__close" type="button" aria-label="Dismiss message" onClick={dismiss}><X /></button>
          <span className="app-alert-dialog__icon" aria-hidden="true">
            {active.tone === "warning" ? <AlertTriangle /> : active.tone === "success" ? <CircleCheck /> : <ShieldAlert />}
          </span>
          <p>{active.tone === "warning" ? "WARNING" : active.tone === "success" ? "COMPLETED" : "ACTION NEEDED"}</p>
          <h2 id="app-alert-title">{active.title}</h2>
          <div id="app-alert-message">{active.message}</div>
          {alerts.length > 1 && <small>{alerts.length - 1} more message{alerts.length === 2 ? "" : "s"} waiting</small>}
          <button className="button button--primary button--full" type="button" onClick={dismiss}>Got it</button>
        </section>
      </div>,
      document.body,
    )}
  </AlertContext.Provider>;
}

export function AppAlert({
  message,
  onDismiss,
  title,
  tone = "error",
}: {
  message: string;
  onDismiss?: () => void;
  title?: string;
  tone?: AppAlertTone;
}) {
  const context = useContext(AlertContext);
  const id = useId();
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  if (!context) throw new Error("AppAlert must be rendered inside AppAlertProvider.");

  useEffect(() => {
    context.register({
      id,
      message,
      title: title ?? (tone === "warning"
        ? "Please review this warning"
        : tone === "success"
          ? "Action completed successfully"
          : "We couldn’t complete that action"),
      tone,
      onDismiss: () => { dismissRef.current?.(); },
    });
    return () => { context.remove(id); };
  }, [context, id, message, title, tone]);

  return null;
}
